import { useCallback, useRef, useState } from 'react'

import { newLotoDraftWithPage } from './lotoPageAddition'
import type {
  LotoAttachment,
  LotoReportEntry,
  LotoReportScope,
} from './lotoReportSchemas'

export type LotoDraftRequest = {
  name: string
  scope: LotoReportScope
  attachment: LotoAttachment
  save: (entry: LotoReportEntry) => Promise<void>
}

export type LotoPageAddition = {
  isOpen: boolean
  creating: boolean
  failedName: string | undefined
  open: () => void
  reset: () => void
  createDraft: (request: LotoDraftRequest) => Promise<string | undefined>
}

export function useLotoPageAddition(): LotoPageAddition {
  const [isOpen, setIsOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [failedName, setFailedName] = useState<string>()
  const attemptRef = useRef(0)
  const creatingRef = useRef(false)
  const retryDraftRef = useRef<LotoReportEntry>()

  const open = useCallback(() => setIsOpen(true), [])
  const reset = useCallback(() => {
    attemptRef.current += 1
    creatingRef.current = false
    setIsOpen(false)
    setCreating(false)
    setFailedName(undefined)
  }, [])
  const createDraft = useCallback(
    async ({
      name,
      scope,
      attachment,
      save,
    }: LotoDraftRequest): Promise<string | undefined> => {
      const trimmed = name.trim()
      if (creatingRef.current || !trimmed) return undefined
      const attempt = ++attemptRef.current
      const isCurrent = (): boolean => attemptRef.current === attempt
      creatingRef.current = true
      setCreating(true)
      setFailedName(undefined)
      const entry = newLotoDraftWithPage(
        trimmed,
        scope,
        attachment,
        retryDraftRef.current
      )
      retryDraftRef.current = entry
      try {
        await save(entry)
        if (retryDraftRef.current === entry) retryDraftRef.current = undefined
        return isCurrent() ? entry.id : undefined
      } catch {
        if (isCurrent()) setFailedName(trimmed)
        return undefined
      } finally {
        if (isCurrent()) {
          creatingRef.current = false
          setCreating(false)
        }
      }
    },
    []
  )
  return { isOpen, creating, failedName, open, reset, createDraft }
}
